<template>
  <EditableRoot
    ref="editableElement"
    v-slot="{ isEditing }"
    :defaultValue="defaultValue"
    @update:modelValue="handleUpdateModelValue($event)"
    :placeholder="placeholder"
    :maxLength="maxLength"
    :autoResize="autoResize"
    :readonly="readonly"
    :activationMode="activationMode"
    :selectOnFocus="selectOnFocus"
    :startWithEditMode="startWithEditMode"
    submitMode="none"
    :class="cn(variants({ variant, size, readonly }))"
    @submit="handleSubmit"
  >
    <div
      v-if="$slots.leading"
      class="absolute inset-y-0 ltr:left-1 rtl:right-1 flex items-center"
    >
      <slot name="leading" />
    </div>
    <EditableArea
      :class="
        cn('flex grow bg-transparent focus-visible:outline-none', props.class)
      "
    >
      <EditablePreview
        ref="previewRef"
        :class="
          cn(
            'grow bg-transparent break-words',
            {
              '!whitespace-pre-wrap': variant !== 'badge',
              'cursor-pointer': !readonly,
            },
            props.class
          )
        "
      />
      <EditableInput
        :as="as"
        :rows="rows"
        :cols="cols"
        :class="
          cn(
            'grow p-0 border-0 bg-transparent placeholder:text-white focus:ring-0 focus-visible:outline-none whitespace-pre-wrap break-words resize-none no-scrollbar',
            props.class
          )
        "
        @keydown.enter="handleEnter"
        @blur="handleBlur"
      />
    </EditableArea>
    <div
      v-show="(showMaxLength || showControls) && isEditing && !readonly"
      class="absolute z-10 flex gap-1 ltr:right-0 rtl:left-0 -bottom-2"
    >
      <div
        v-if="maxLength"
        class="text-xs pb-0 pt-1 px-1.5 font-semibold bg-primary border border-secondary rounded-sm shadow-md text-secondary"
      >
        {{ count }} / {{ maxLength }}
      </div>
      <EditableSubmitTrigger
        v-show="showControls"
        :id="buttonIds[0]"
        class="flex justify-end gap-2 p-1 bg-primary border border-secondary rounded-sm hover:bg-tertiary shadow-md text-secondary cursor-pointer hover:shadow-lg"
      >
        <Icon name="checkmark-default" />
      </EditableSubmitTrigger>
      <EditableCancelTrigger
        v-show="showControls"
        :id="buttonIds[1]"
        class="flex justify-end gap-2 p-1 bg-primary border border-secondary rounded-sm hover:bg-tertiary shadow-md text-secondary cursor-pointer hover:shadow-lg"
      >
        <Icon name="close" />
      </EditableCancelTrigger>
    </div>
  </EditableRoot>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, nextTick } from "vue";
import { uniqueId } from "lodash";
import {
  EditableArea,
  EditableCancelTrigger,
  EditableInput,
  EditablePreview,
  EditableRoot,
  EditableSubmitTrigger,
} from "radix-vue";
import { Icon } from "@/SharedComponents/Icon";
import { editableTextAreaVariants as variants } from "@/SharedComponents/Inputs";
import { cn } from "@/Utils/cn";

const emit = defineEmits(["update:modelValue", "update"]);

const previewRef = ref(null);

const props = defineProps({
  defaultValue: { type: String, default: null },
  rows: { type: [Number, String], default: 1 },
  cols: { type: [Number, String], default: 20 },
  maxLength: { type: [String, Number], required: false },
  as: { type: String, default: "textarea" },
  variant: { type: String, required: false },
  size: { type: String, required: false },
  placeholder: { type: String, required: false },
  readonly: { type: Boolean, default: false },
  autoResize: { type: Boolean, default: true },
  activationMode: { type: String, default: "focus" },
  startWithEditMode: { type: Boolean, required: false },
  selectOnFocus: { type: Boolean, required: false },
  showMaxLength: { type: Boolean, default: true },
  submitMode: {
    type: String,
    validator: (value) => ["blur", "none", "enter", "both"].includes(value),
    default: "blur",
  },
  controls: { type: Boolean, required: false },
  class: { type: null, required: false },
});

const showMaxLength = computed(() => props.maxLength && props.showMaxLength);

const showControls = computed(
  () => props.controls || props.submitMode === "none"
);

function handleSubmit(value) {
  const initialValue = props.defaultValue ?? "";

  if (initialValue !== value?.trim()) {
    emit("update", value ? value.trim() : "");
  }
}

const clickedElement = ref(null);
const editableElement = ref(null);

function handleMouseDown(event) {
  clickedElement.value = event.target.closest("button");
}

const enter = ref(false);
const count = ref(props.defaultValue ? props.defaultValue.length : 0);
const buttonIds = [uniqueId(), uniqueId()];

const handleUpdateModelValue = (value) => {
  count.value = value.length;
  emit("update:modelValue", value);
};

function handleEnter() {
  if (props.submitMode === "enter" || props.submitMode === "both") {
    enter.value = true;
    editableElement.value?.submit();
  }
}

function handleBlur(event) {
  const targetElement =
    clickedElement.value || event?.relatedTarget?.closest("button");
  if (enter.value || buttonIds.includes(targetElement?.id)) {
    enter.value = false;
    return;
  }
  if (props.submitMode === "blur" || props.submitMode === "both") {
    editableElement.value?.submit();
  } else {
    editableElement.value?.cancel();
  }
}

onMounted(async () => {
  document.addEventListener("mousedown", handleMouseDown);
  if (props.startWithEditMode && editableElement.value) {
    await nextTick();
    editableElement.value.edit();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleMouseDown);
});

defineExpose({
  previewRef,
  edit: () => {
    editableElement.value.edit();
  },
});
</script>
